<script lang="ts">import { Button } from "@infinitro/button";
import { stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { hexToRgba, isAbsoluteHttpUrl, loggedIn, ModalDialog, pushModal, toSiteUrl, toTitleCase } from "@infinitro/global";
import { Text } from "@infinitro/text";
export let backgroundImage = "";
export let backgroundColor = "";
export let image = "";
export let title = "";
export let description = "";
export let badgeText;
export let buttonText = "";
export let buttonVariant = "tertiary";
export let imageStyles = "";
export let link = "";
export let soon = false;
export let multiLineDescription = false;
export let titleAllHighlighted = false;
export let customTitleWidth = null;
export let customDescriptionWidth = null;
export let requireLogIn = false;
export let titleSingleLine = false;
export let titleLogo = undefined;
export let onActionClick = undefined;
export let amountBoxText = undefined;
export let amountBoxColor = undefined;
export let amountBoxTextColor = undefined;
$: hasAmountBox = !!amountBoxText;
$: resolvedAmountBoxColor = amountBoxColor ?? "var(--color-text-100)";
$: resolvedAmountBoxTextColor = amountBoxTextColor ?? "var(--color-background-900)";
const TOP_SECTION_BUTTON_VARIANTS = [
	"primary",
	"secondary",
	"tertiary"
];
let titleParts = [];
let groupedTitleParts = [];
let titleRows = [];
const parseTitleParts = (rawTitle) => {
	const angleMatch = rawTitle.match(/^(.*?)\(\((.+?)\)\)(.*)$/);
	if (angleMatch) {
		const before = angleMatch[1].trim();
		const inside = angleMatch[2].trim();
		const after = angleMatch[3].trim();
		return [
			...before.split(" ").filter(Boolean).map((w) => ({
				text: w,
				small: false
			})),
			{
				text: inside,
				small: true
			},
			...after.split(" ").filter(Boolean).map((w) => ({
				text: w,
				small: false
			}))
		];
	}
	if (rawTitle.includes("[") && rawTitle.includes("]")) {
		const match = rawTitle.match(/\[([^\]]+)\](.*)/);
		if (match) {
			return [{
				text: match[1].trim(),
				small: false
			}, ...match[2].trim().split(" ").filter(Boolean).map((w) => ({
				text: w,
				small: false
			}))];
		}
	}
	return rawTitle.split(" ").filter(Boolean).map((w) => ({
		text: w,
		small: false
	}));
};
$: {
	titleRows = (title ?? "").split("\n").map((line) => line.trim()).filter(Boolean);
	titleParts = parseTitleParts(title ?? "");
}
const chunkByTwo = (parts) => {
	const chunks = [];
	for (let i = 0; i < parts.length; i += 2) {
		chunks.push(parts.slice(i, i + 2));
	}
	return chunks;
};
$: {
	if (titleRows.length > 1) {
		groupedTitleParts = titleRows.map(parseTitleParts).filter((row) => row.length > 0);
	} else {
		const whiteWords = titleParts.slice(1);
		const shouldGroupWhiteWords = !titleAllHighlighted && whiteWords.length > 2;
		if (titleParts.length === 0) {
			groupedTitleParts = [];
		} else if (titleSingleLine || hasAmountBox) {
			groupedTitleParts = [titleParts];
		} else if (shouldGroupWhiteWords) {
			groupedTitleParts = [[titleParts[0]], ...chunkByTwo(whiteWords)];
		} else {
			groupedTitleParts = titleParts.map((part) => [part]);
		}
	}
}
$: cardHref = toSiteUrl(link, $loggedIn) || "#";
$: cardHrefExternal = isAbsoluteHttpUrl(cardHref);
const redirect = (url) => {
	toSiteUrl(url, $loggedIn);
};
const highlightBrackets = (str) => {
	return useAlternative2 ? str : (str ?? "").replace(/\[([^\]]+)\]/g, "<span style=\"color: var(--color-highlight-1)\">$1</span>");
};
$: normalizedDescription = (description ?? "").replace(/\\n/g, "\n");
$: processedDescription = highlightBrackets(normalizedDescription);
$: processedLines = normalizedDescription.split("\n").map(highlightBrackets);
$: showSignUp = requireLogIn && !$loggedIn;
$: useAlternative2 = $stateUi.topSection.casinoTopSectionStyle === "alternative2";
$: useTitleHighlight = $stateUi.topSection.highlightTitleFirstWord ?? true;
$: useUppercaseSectionTitles = $stateUi.layout.useUppercaseSectionTitles ?? true;
function formatSectionTitleWord(text) {
	return useUppercaseSectionTitles ? text : toTitleCase(text);
}
$: rawTopSectionButtonVariant = Reflect.get($stateUi.topSection, "buttonVariant");
$: stateUiButtonVariant = TOP_SECTION_BUTTON_VARIANTS.includes(rawTopSectionButtonVariant) ? rawTopSectionButtonVariant : undefined;
$: resolvedButtonVariant = stateUiButtonVariant ?? (useAlternative2 ? "primary" : buttonVariant);
$: backgroundGradientTextStyle = useAlternative2 ? `background-image: linear-gradient(90deg, var(--color-text-100) 0%, var(--color-text-100) 100%);` : "";
$: descriptionDefaultWidth = useAlternative2 ? "100%" : "75%";
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
	class="casino-top-card"
	style="background-image: url('{backgroundImage}'); background-color: {backgroundColor};"
	on:click={!buttonText && !soon
		? async (e) => {
				if (onActionClick) {
					e.preventDefault();
					await onActionClick();
					return;
				}
				redirect(link);
			}
		: undefined}
>
	<div class="casino-top-card-content" class:casino-top-card-content-alternative2={useAlternative2}>
		{#if useAlternative2}
			<div class="casino-top-card-badge">
				<p>
					{useUppercaseSectionTitles
						? (badgeText ?? $t(Verbiage.promotion))
						: toTitleCase(badgeText ?? $t(Verbiage.promotion))}
				</p>
			</div>
		{/if}
		<div style="max-height: 75%;">
			{#if soon}
				<h2
					class="casino-top-card-title"
					class:casino-top-card-title-alternative2={useAlternative2}
					class:casino-top-card-title-uppercase={useUppercaseSectionTitles}
					style="width: {customTitleWidth ?? ''}"
				>
					{#each groupedTitleParts as row, rowIndex}
						{#if rowIndex > 0}<br />{/if}
						<span class="casino-top-card-title-row">
							{#if rowIndex === 0 && titleLogo}
								<img src={titleLogo} alt="" class="casino-top-card-title-logo" />
							{/if}
							{#each row as part, partIndex}
								<span
									class:highlighted={useTitleHighlight &&
										((rowIndex === 0 && partIndex === 0) || titleAllHighlighted) &&
										!useAlternative2}
									class:alternative2-title-word={useAlternative2}
									class:title-small={part.small}
									style={backgroundGradientTextStyle}
								>
									{formatSectionTitleWord(part.text)}{partIndex < row.length - 1 ? ' ' : ''}
								</span>
							{/each}
						</span>
					{/each}
				</h2>
			{:else}
				<a
					href={cardHref}
					target={cardHrefExternal ? '_blank' : '_self'}
					rel={cardHrefExternal ? 'noopener noreferrer' : undefined}
					on:click={async (e) => {
						if (onActionClick) {
							e.preventDefault();
							await onActionClick();
						}
					}}
				>
					<h2
						class="casino-top-card-title"
						class:casino-top-card-title-alternative2={useAlternative2}
						class:casino-top-card-title-uppercase={useUppercaseSectionTitles}
						style="width: {customTitleWidth ?? ''}"
					>
						{#each groupedTitleParts as row, rowIndex}
							{#if rowIndex > 0}<br />{/if}
							<span class="casino-top-card-title-row">
								{#if rowIndex === 0 && titleLogo}
									<img src={titleLogo} alt="" class="casino-top-card-title-logo" />
								{/if}
								{#each row as part, partIndex}
									<span
										class:highlighted={useTitleHighlight &&
											((rowIndex === 0 && partIndex === 0) || titleAllHighlighted) &&
											!useAlternative2}
										class:alternative2-title-word={useAlternative2}
										class:title-small={part.small}
										style={backgroundGradientTextStyle}
									>
										{formatSectionTitleWord(part.text)}{partIndex < row.length - 1 ? ' ' : ''}
									</span>
								{/each}
							</span>
						{/each}
					</h2>
				</a>
			{/if}
			{#if hasAmountBox}
				<div
					class="casino-top-card-amount-box"
					style="background-color: {resolvedAmountBoxColor}; color: {resolvedAmountBoxTextColor};"
				>
					{amountBoxText}
				</div>
			{/if}
			<p
				class="casino-top-card-description"
				class:casino-top-card-description-alternative2={useAlternative2}
				style="width: {customDescriptionWidth ?? descriptionDefaultWidth}"
			>
				{#if multiLineDescription}
					{#each processedLines as line, i}
						{@html line}
						{#if i < processedLines.length - 1}<br />{/if}
					{/each}
				{:else}
					{@html processedDescription}
				{/if}
			</p>
		</div>
		{#if buttonText && !showSignUp}
			{#if onActionClick}
				<div class="casino-top-card-link" class:casino-top-card-link-soon={soon}>
					<Button
						text={buttonText}
						variant={resolvedButtonVariant}
						fullWidth={!useAlternative2}
						disabledWithoutStyles={soon}
						size={useAlternative2 ? 'small' : 'flexible'}
						onClick={soon
							? undefined
							: async (e) => {
									e.stopPropagation();
									await onActionClick();
								}}
					/>
				</div>
			{:else}
				<a
					class="casino-top-card-link"
					class:casino-top-card-link-soon={soon}
					href={soon ? undefined : cardHref}
					target={cardHrefExternal ? '_blank' : '_self'}
					rel={cardHrefExternal ? 'noopener noreferrer' : undefined}
					on:click={(e) => {
						if (soon) e.preventDefault();
					}}
				>
					<Button
						text={buttonText}
						variant={resolvedButtonVariant}
						fullWidth={!useAlternative2}
						disabledWithoutStyles={soon}
						size={useAlternative2 ? 'small' : 'flexible'}
					/>
				</a>
			{/if}
		{:else if showSignUp}
			<div class="casino-top-card-link">
				<Button
					text={soon ? $t(Verbiage.coming_soon) : $t(Verbiage.sign_up)}
					variant={resolvedButtonVariant}
					uppercase={true}
					fullWidth
					disabledWithoutStyles={soon}
					size="flexible"
					onClick={soon ? undefined : () => pushModal(ModalDialog.Auth, { tab: 'signup' })}
				/>
			</div>
		{/if}
	</div>
	<!-- {#if image}
		<div class="casino-top-card-image-container">
			<img
				src={image}
				alt={$t(Verbiage.section_icon_alt, 'alts')}
				class="casino-top-card-illustration"
				style={imageStyles}
			/>
		</div>
	{/if} -->
</div>

<style>
	.casino-top-card {
		position: relative;
		display: flex;
		height: 100%;
		width: 100%;
		cursor: grab;
		flex-direction: column;
		align-items: flex-start;
		justify-content: space-between;
		border-radius: 16px;
		background-color: var(--color-background-900);
		background-size: cover;
		background-position: center;
		aspect-ratio: 3 / 1.6;
		container-type: size;
}

	@media (min-width: 1280px) {
		.casino-top-card {
				max-height: 230px;
				min-height: 210px;
		}
	}

	@media (min-width: 1024px) {
		.casino-top-card {
			aspect-ratio: 3 / 2;
		}
	}

	.casino-top-card-content {
		position: relative;
		z-index: 10;
		display: flex;
		height: 100%;
		width: 80%;
		flex-direction: column;
		align-items: flex-start;
		justify-content: space-between;
		gap: 0.25rem;
		padding: 5%;
		padding-right: 0px;
}

	@media (min-width: 1600px) {
		.casino-top-card-content {
				width: 70%;
				padding-right: 1.25rem;
		}
	}

	.casino-top-card-title {
		margin-bottom: 2%;
		font-size: 18px;
		font-weight: 800;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		font-family: var(--font-alternative-2);
		text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.5);
		line-height: 1.2;
		font-size: clamp(12px, 5cqi, 20px) !important;
}

	@media (min-width: 1600px) {
		.casino-top-card-title {
				font-size: 20px;
		}
	}

	.casino-top-card-title-alternative2 {
		font-size: clamp(12px, 5cqi, 24px) !important;
		line-height: 1.1;
		font-weight: 700;
		letter-spacing: -2%;
	}

	.casino-top-card-title-row {
		display: inline-flex;
		align-items: center;
		gap: 0.35em;
	}

	.casino-top-card-title-logo {
		height: 1em;
		width: auto;
		flex-shrink: 0;
	}

	.highlighted {
		color: var(--color-highlight-1);
}

	.title-small {
		font-size: clamp(8px, 3.5cqi, 14px) !important;
		white-space: nowrap;
	}

	.casino-top-card-amount-box {
		margin-bottom: 2%;
		display: inline-block;
		width: -moz-fit-content;
		width: fit-content;
		border-radius: 6.5px;
		padding-left: 0.25rem;
		padding-right: 0.25rem;
		padding-top: 0.125rem;
		padding-bottom: 0.125rem;
		font-weight: 800;
		font-family: var(--font-alternative-2);
		font-size: clamp(12px, 5cqi, 20px) !important;
		line-height: 1.3;
}

	.casino-top-card-description {
		margin-bottom: 2%;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		font-family: var(--font-alternative-2);
		text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.8);
		font-size: clamp(7px, 3.5cqi, 13px) !important;
		line-height: clamp(11px, 5cqi, 16px) !important;
}

	@media (min-width: 1024px) {
		.casino-top-card-description {
				font-size: 12px;
		}
	}

	@media (min-width: 1600px) {
		.casino-top-card-description {
				font-size: 14px;
		}
	}

	.casino-top-card-description-alternative2 {
		margin-top: 5%;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 600;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		letter-spacing: 0.5%;
}

	.casino-top-card-description :global(.promotion-period-range-label) {
		opacity: 0.8;
		font-weight: 400;
	}

	.casino-top-card-description :global(.promotion-period-range-value) {
		opacity: 1;
		font-weight: 700;
	}

	.casino-top-card-link {
		height: 25%;
		max-height: 40px;
		min-width: 60%;
}

	.casino-top-card-link-soon {
		cursor: default;
	}

	.casino-top-card-image-container {
		position: absolute;
		top: 0px;
		right: 0px;
		display: flex;
		height: 100%;
		align-items: center;
		justify-content: flex-end;
}

	.casino-top-card-illustration {
		height: 100%;
		width: auto;
		max-width: none;
}

	.soon {
		font-size: 12px;
		font-weight: 600;
		opacity: 0.6;
}

	.casino-top-card-content-alternative2 {
		gap: 0.75rem;
		padding: 1rem;
		max-width: 50%;
}

	.casino-top-card-title-uppercase {
		text-transform: uppercase;
}

	.casino-top-card-badge {
		width: -moz-max-content;
		width: max-content;
		max-width: 70cqw;
		align-self: flex-start;
		border-radius: 4px;
		padding-left: 0.5rem;
		padding-right: 0.5rem;
		padding-top: 0.125rem;
		padding-bottom: 0.125rem;
		font-size: 12px;
		font-weight: 800;
		text-transform: uppercase;
		--tw-text-opacity: 1;
		color: rgb(0 0 0 / var(--tw-text-opacity, 1));
		font-family: var(--font-main);
		background: #ffffff;
}

	.casino-top-card-badge p {
		margin: 0;
		line-height: 1.2;
	}

	.alternative2-title-word {
		-webkit-background-clip: text;
		background-clip: text;
		color: transparent;
		text-shadow: none;
	}

	.casino-top-card-content-alternative2 .casino-top-card-link :global(.infinitro-button) {
		padding-left: 30px;
		padding-right: 30px;
}
</style>
